import * as React from 'react';
import { IgcPivotGridComponent as Component } from 'igniteui-webcomponents-grids/grids/index.js';
import type { IgcPivotGridComponentEventMap } from 'igniteui-webcomponents-grids/grids/index.js';
import { type EventName, createComponent } from '../react-props.js';

/**
 * Pivot Grid provides a way to present and manipulate data in a pivot table view.
 * @fires dimensionsChange - Emitted when the dimension collection is changed via the grid chip area.
 * @fires pivotConfigurationChange - Emitted when any of the pivotConfiguration properties is changed via the grid chip area.
 * @fires dimensionInit - Emitted when the dimension is initialized.
 * @fires valueInit - Emitted when the value is initialized.
 * @fires dimensionsSortingExpressionsChange - Emitted when a dimension is sorted.
 * @fires valuesChange - Emitted when the values collection is changed via the grid chip area.
 * @fires filteringExpressionsTreeChange - Emitted after filtering is performed.
 * @fires advancedFilteringExpressionsTreeChange - Emitted after advanced filtering is performed.
 * @fires gridScroll - Emitted when grid is scrolled horizontally/vertically.
 * @fires cellClick - Emitted when a cell is clicked.
 * @fires rowClick - Emitted when a row is clicked.
 * @fires formGroupCreated - Emitted when formGroup is created on edit of row/cell.
 * @fires validationStatusChange - Emitted when grid's validation status changes.
 * @fires selected - Emitted when a cell is selected.
 * @fires rowSelectionChanging - Emitted when `IgxGridRowComponent` is selected.
 * @fires columnSelectionChanging - Emitted when `IgxColumnComponent` is selected.
 * @fires columnPin - Emitted before `IgxColumnComponent` is pinned.
 * @fires columnPinned - Emitted after `IgxColumnComponent` is pinned.
 * @fires cellEditEnter - Emitted when cell enters edit mode.
 * @fires cellEditExit - Emitted when cell exits edit mode.
 * @fires cellEdit - Emitted when cell has been edited.
 * @fires cellEditDone - Emitted after cell has been edited and editing has been committed.
 * @fires rowEditEnter - Emitted when a row enters edit mode.
 * @fires rowEdit - Emitted when exiting edit mode for a row.
 * @fires rowEditDone - Emitted after exiting edit mode for a row and editing has been committed.
 * @fires rowEditExit - Emitted when row editing is canceled.
 * @fires columnInit - Emitted when a column is initialized.
 * @fires columnsAutogenerated -
 * @fires sorting - Emitted before sorting expressions are applied.
 * @fires sortingDone - Emitted after sorting is completed.
 * @fires filtering - Emitted before filtering expressions are applied.
 * @fires filteringDone - Emitted after filtering is performed through the UI.
 * @fires rowAdded - Emitted when a row is added.
 * @fires rowDeleted - Emitted when a row is deleted.
 * @fires rowDelete - Emmited when deleting a row.
 * @fires rowAdd - Emmited just before the newly added row is commited.
 * @fires columnResized - Emitted after column is resized.
 * @fires contextMenu - Emitted when a cell or row is right clicked.
 * @fires doubleClick - Emitted when a cell is double clicked.
 * @fires columnVisibilityChanging - Emitted before column visibility is changed.
 * @fires columnVisibilityChanged - Emitted after column visibility is changed.
 * @fires columnMovingStart - Emitted when column moving starts.
 * @fires columnMoving - Emitted during the column moving operation.
 * @fires columnMovingEnd - Emitted when column moving ends.
 * @fires gridKeydown - Emitted when keydown is triggered over element inside grid's body.
 * @fires rowDragStart - Emitted when start dragging a row.
 * @fires rowDragEnd - Emitted when dropping a row.
 * @fires gridCopy - Emitted when a copy operation is executed.
 * @fires expansionStatesChange - Emitted when the rows are expanded or collapsed.
 * @fires selectedRowsChange - Emitted when the rows are selected or deselected.
 * @fires rowToggle - Emitted when the expanded state of a row gets changed.
 * @fires rowPinning - Emitted when the pinned state of a row is changed.
 * @fires rowPinned - Emitted when the pinned state of a row is changed.
 * @fires activeNodeChange - Emitted when the active node is changed.
 * @fires sortingExpressionsChange - Emitted before sorting is performed.
 * @fires toolbarExporting - Emitted when an export process is initiated by the user.
 * @fires rangeSelected - Emitted when making a range selection.
 * @fires rendered - Emitted after the ngAfterViewInit hook. At this point the grid exists in the DOM
 * @fires dataChanging - Emitted before the grid's data view is changed because of a data operation, rebinding, etc.
 * @fires dataChanged - Emitted after the grid's data view is changed because of a data operation, rebinding, etc.
 * @class
 */
export const IgrPivotGrid = /* @__PURE__ */ createComponent({
  tagName: 'igc-pivot-grid',
  displayName: 'IgrPivotGrid',
  elementClass: Component,
  react: React,
  moveBackOnDelete: true,
  events: {
    onDimensionsChange: 'dimensionsChange' as EventName<
      IgcPivotGridComponentEventMap['dimensionsChange']
    >,
    onPivotConfigurationChange: 'pivotConfigurationChange' as EventName<
      IgcPivotGridComponentEventMap['pivotConfigurationChange']
    >,
    onDimensionInit: 'dimensionInit' as EventName<
      IgcPivotGridComponentEventMap['dimensionInit']
    >,
    onValueInit: 'valueInit' as EventName<
      IgcPivotGridComponentEventMap['valueInit']
    >,
    onDimensionsSortingExpressionsChange:
      'dimensionsSortingExpressionsChange' as EventName<
        IgcPivotGridComponentEventMap['dimensionsSortingExpressionsChange']
      >,
    onValuesChange: 'valuesChange' as EventName<
      IgcPivotGridComponentEventMap['valuesChange']
    >,
    onFilteringExpressionsTreeChange:
      'filteringExpressionsTreeChange' as EventName<
        IgcPivotGridComponentEventMap['filteringExpressionsTreeChange']
      >,
    onAdvancedFilteringExpressionsTreeChange:
      'advancedFilteringExpressionsTreeChange' as EventName<
        IgcPivotGridComponentEventMap['advancedFilteringExpressionsTreeChange']
      >,
    onGridScroll: 'gridScroll' as EventName<
      IgcPivotGridComponentEventMap['gridScroll']
    >,
    onCellClick: 'cellClick' as EventName<
      IgcPivotGridComponentEventMap['cellClick']
    >,
    onRowClick: 'rowClick' as EventName<
      IgcPivotGridComponentEventMap['rowClick']
    >,
    onFormGroupCreated: 'formGroupCreated' as EventName<
      IgcPivotGridComponentEventMap['formGroupCreated']
    >,
    onValidationStatusChange: 'validationStatusChange' as EventName<
      IgcPivotGridComponentEventMap['validationStatusChange']
    >,
    onSelected: 'selected' as EventName<
      IgcPivotGridComponentEventMap['selected']
    >,
    onRowSelectionChanging: 'rowSelectionChanging' as EventName<
      IgcPivotGridComponentEventMap['rowSelectionChanging']
    >,
    onColumnSelectionChanging: 'columnSelectionChanging' as EventName<
      IgcPivotGridComponentEventMap['columnSelectionChanging']
    >,
    onColumnPin: 'columnPin' as EventName<
      IgcPivotGridComponentEventMap['columnPin']
    >,
    onColumnPinned: 'columnPinned' as EventName<
      IgcPivotGridComponentEventMap['columnPinned']
    >,
    onCellEditEnter: 'cellEditEnter' as EventName<
      IgcPivotGridComponentEventMap['cellEditEnter']
    >,
    onCellEditExit: 'cellEditExit' as EventName<
      IgcPivotGridComponentEventMap['cellEditExit']
    >,
    onCellEdit: 'cellEdit' as EventName<
      IgcPivotGridComponentEventMap['cellEdit']
    >,
    onCellEditDone: 'cellEditDone' as EventName<
      IgcPivotGridComponentEventMap['cellEditDone']
    >,
    onRowEditEnter: 'rowEditEnter' as EventName<
      IgcPivotGridComponentEventMap['rowEditEnter']
    >,
    onRowEdit: 'rowEdit' as EventName<IgcPivotGridComponentEventMap['rowEdit']>,
    onRowEditDone: 'rowEditDone' as EventName<
      IgcPivotGridComponentEventMap['rowEditDone']
    >,
    onRowEditExit: 'rowEditExit' as EventName<
      IgcPivotGridComponentEventMap['rowEditExit']
    >,
    onColumnInit: 'columnInit' as EventName<
      IgcPivotGridComponentEventMap['columnInit']
    >,
    onColumnsAutogenerated: 'columnsAutogenerated' as EventName<
      IgcPivotGridComponentEventMap['columnsAutogenerated']
    >,
    onSorting: 'sorting' as EventName<IgcPivotGridComponentEventMap['sorting']>,
    onSortingDone: 'sortingDone' as EventName<
      IgcPivotGridComponentEventMap['sortingDone']
    >,
    onFiltering: 'filtering' as EventName<
      IgcPivotGridComponentEventMap['filtering']
    >,
    onFilteringDone: 'filteringDone' as EventName<
      IgcPivotGridComponentEventMap['filteringDone']
    >,
    onRowAdded: 'rowAdded' as EventName<
      IgcPivotGridComponentEventMap['rowAdded']
    >,
    onRowDeleted: 'rowDeleted' as EventName<
      IgcPivotGridComponentEventMap['rowDeleted']
    >,
    onRowDelete: 'rowDelete' as EventName<
      IgcPivotGridComponentEventMap['rowDelete']
    >,
    onRowAdd: 'rowAdd' as EventName<IgcPivotGridComponentEventMap['rowAdd']>,
    onColumnResized: 'columnResized' as EventName<
      IgcPivotGridComponentEventMap['columnResized']
    >,
    onContextMenu: 'contextMenu' as EventName<
      IgcPivotGridComponentEventMap['contextMenu']
    >,
    onDoubleClick: 'doubleClick' as EventName<
      IgcPivotGridComponentEventMap['doubleClick']
    >,
    onColumnVisibilityChanging: 'columnVisibilityChanging' as EventName<
      IgcPivotGridComponentEventMap['columnVisibilityChanging']
    >,
    onColumnVisibilityChanged: 'columnVisibilityChanged' as EventName<
      IgcPivotGridComponentEventMap['columnVisibilityChanged']
    >,
    onColumnMovingStart: 'columnMovingStart' as EventName<
      IgcPivotGridComponentEventMap['columnMovingStart']
    >,
    onColumnMoving: 'columnMoving' as EventName<
      IgcPivotGridComponentEventMap['columnMoving']
    >,
    onColumnMovingEnd: 'columnMovingEnd' as EventName<
      IgcPivotGridComponentEventMap['columnMovingEnd']
    >,
    onGridKeydown: 'gridKeydown' as EventName<
      IgcPivotGridComponentEventMap['gridKeydown']
    >,
    onRowDragStart: 'rowDragStart' as EventName<
      IgcPivotGridComponentEventMap['rowDragStart']
    >,
    onRowDragEnd: 'rowDragEnd' as EventName<
      IgcPivotGridComponentEventMap['rowDragEnd']
    >,
    onGridCopy: 'gridCopy' as EventName<
      IgcPivotGridComponentEventMap['gridCopy']
    >,
    onExpansionStatesChange: 'expansionStatesChange' as EventName<
      IgcPivotGridComponentEventMap['expansionStatesChange']
    >,
    onSelectedRowsChange: 'selectedRowsChange' as EventName<
      IgcPivotGridComponentEventMap['selectedRowsChange']
    >,
    onRowToggle: 'rowToggle' as EventName<
      IgcPivotGridComponentEventMap['rowToggle']
    >,
    onRowPinning: 'rowPinning' as EventName<
      IgcPivotGridComponentEventMap['rowPinning']
    >,
    onRowPinned: 'rowPinned' as EventName<
      IgcPivotGridComponentEventMap['rowPinned']
    >,
    onActiveNodeChange: 'activeNodeChange' as EventName<
      IgcPivotGridComponentEventMap['activeNodeChange']
    >,
    onSortingExpressionsChange: 'sortingExpressionsChange' as EventName<
      IgcPivotGridComponentEventMap['sortingExpressionsChange']
    >,
    onToolbarExporting: 'toolbarExporting' as EventName<
      IgcPivotGridComponentEventMap['toolbarExporting']
    >,
    onRangeSelected: 'rangeSelected' as EventName<
      IgcPivotGridComponentEventMap['rangeSelected']
    >,
    onRendered: 'rendered' as EventName<
      IgcPivotGridComponentEventMap['rendered']
    >,
    onDataChanging: 'dataChanging' as EventName<
      IgcPivotGridComponentEventMap['dataChanging']
    >,
    onDataChanged: 'dataChanged' as EventName<
      IgcPivotGridComponentEventMap['dataChanged']
    >,
  },
  renderProps: {
    valueChipTemplate: 'valueChipTemplate',
    rowDimensionHeaderTemplate: 'rowDimensionHeaderTemplate',
    emptyPivotGridTemplate: 'emptyPivotGridTemplate',
    emptyGridTemplate: 'emptyGridTemplate',
    addRowEmptyTemplate: 'addRowEmptyTemplate',
    loadingGridTemplate: 'loadingGridTemplate',
    dragGhostCustomTemplate: 'dragGhostCustomTemplate',
    rowEditTextTemplate: 'rowEditTextTemplate',
    rowAddTextTemplate: 'rowAddTextTemplate',
    rowEditActionsTemplate: 'rowEditActionsTemplate',
    rowExpandedIndicatorTemplate: 'rowExpandedIndicatorTemplate',
    rowCollapsedIndicatorTemplate: 'rowCollapsedIndicatorTemplate',
    headerExpandedIndicatorTemplate: 'headerExpandedIndicatorTemplate',
    headerCollapsedIndicatorTemplate: 'headerCollapsedIndicatorTemplate',
    excelStyleHeaderIconTemplate: 'excelStyleHeaderIconTemplate',
    sortAscendingHeaderIconTemplate: 'sortAscendingHeaderIconTemplate',
    sortDescendingHeaderIconTemplate: 'sortDescendingHeaderIconTemplate',
    sortHeaderIconTemplate: 'sortHeaderIconTemplate',
    headSelectorTemplate: 'headSelectorTemplate',
    rowSelectorTemplate: 'rowSelectorTemplate',
    dragIndicatorIconTemplate: 'dragIndicatorIconTemplate',
  },
});

export type IgrPivotGrid = Component;

/** @deprecated Module register is no longer needed and can be removed */
export const IgrPivotGridModule = Component;
